import { useEffect, useRef, useState } from 'react';
import { getOpenPaymentItems, mapPaymentError } from '../../api/endpoints/payments';
import type { OpenItemsResponse, PaymentOpenItemOrder } from '../../api/types';

export type OpenItemsLoader = typeof getOpenPaymentItems;

// Selection is independent of the current search/page. Store order metadata too,
// so a selected allocation stays editable even when its order is not a result.
export function useSelectedOpenItems<Row extends { shipmentId: number }>(
  items: OpenItemsResponse,
  createRow: (order: PaymentOpenItemOrder, previous?: Row) => Row,
  hasValue: (row: Row) => boolean,
) {
  type Entry = { order: PaymentOpenItemOrder; row: Row };
  const [entries, setEntries] = useState(
    () =>
      new Map<number, Entry>(
        items.orders.map((order) => [order.shipmentId, { order, row: createRow(order) }]),
      ),
  );
  const counterparty = useRef(items.counterpartyId);
  useEffect(() => {
    const changedPartner = counterparty.current !== items.counterpartyId;
    counterparty.current = items.counterpartyId;
    setEntries((previous) => {
      const current = changedPartner ? new Map<number, Entry>() : previous;
      const next = new Map([...current].filter(([, entry]) => hasValue(entry.row)));
      items.orders.forEach((order) => {
        const old = current.get(order.shipmentId)?.row;
        next.set(order.shipmentId, { order, row: createRow(order, old) });
      });
      return next;
    });
  }, [items, createRow, hasValue]);
  const selected = new Map([...entries].filter(([, entry]) => hasValue(entry.row)));
  const orders = [...selected.values()]
    .map((entry) => entry.order)
    .concat(items.orders.filter((order) => !selected.has(order.shipmentId)));
  const setRows = (rows: Row[]) =>
    setEntries((previous) => {
      const next = new Map(previous);
      rows.forEach((row) => {
        const entry = next.get(row.shipmentId);
        if (!entry) return;
        if (!hasValue(row) && !items.orders.some((order) => order.shipmentId === row.shipmentId))
          next.delete(row.shipmentId);
        else next.set(row.shipmentId, { ...entry, row });
      });
      return next;
    });
  return {
    orders,
    selected,
    rows: [...entries.values()].map((entry) => entry.row),
    setRows,
    clear: () =>
      setEntries(
        new Map(items.orders.map((order) => [order.shipmentId, { order, row: createRow(order) }])),
      ),
  };
}

export function useOpenItemsPaging(
  initial: OpenItemsResponse,
  load: OpenItemsLoader = getOpenPaymentItems,
  initialShipmentIds: number[] = [],
) {
  const initialKey = initialShipmentIds.join(',');
  const [prefillError, setPrefillError] = useState<string | null>(null);
  const [items, setItems] = useState(initial);
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lifecycle = useRef({ active: true });
  const busy = useRef(false);
  useEffect(() => {
    const current = { active: true };
    lifecycle.current = current;
    busy.current = false;
    setItems(initial);
    setSearch('');
    setQuery('');
    setPending(false);
    setError(null);
    setPrefillError(null);
    const missing = initialKey
      .split(',')
      .filter(Boolean)
      .map(Number)
      .filter((id) => !initial.orders.some((order) => order.shipmentId === id));
    if (missing.length) {
      busy.current = true;
      setPending(true);
      void Promise.all(
        missing.map(async (id) => {
          const result = await load(initial.direction, initial.counterpartyId, {
            page: 0,
            size: 50,
            q: `#${id}`,
          });
          const order = result.orders.find((order) => order.shipmentId === id);
          if (!order)
            throw new Error(
              `Order #${id} is no longer available for this payout. Close and refresh the queue.`,
            );
          return order;
        }),
      )
        .then((orders) => {
          if (current.active) setItems({ ...initial, orders: [...initial.orders, ...orders] });
        })
        .catch((error) => {
          if (current.active) setPrefillError(mapPaymentError(error));
        })
        .finally(() => {
          if (current.active) {
            busy.current = false;
            setPending(false);
          }
        });
    }
    return () => {
      current.active = false;
    };
  }, [initial, initialKey, load]);

  const fetchPage = async (more: boolean) => {
    if (busy.current) return;
    busy.current = true;
    setPending(true);
    setError(null);
    const current = lifecycle.current;
    const q = more ? query : search.trim();
    try {
      const next = await load(initial.direction, initial.counterpartyId, {
        page: more ? items.page + 1 : 0,
        size: items.size,
        q,
      });
      if (!current.active) return;
      setItems((current) => ({
        ...next,
        orders: more
          ? [
              ...new Map(
                [...current.orders, ...next.orders].map((order) => [order.shipmentId, order]),
              ).values(),
            ]
          : next.orders,
      }));
      setQuery(q);
    } catch (caught) {
      if (current.active) setError(mapPaymentError(caught));
    } finally {
      if (current.active) {
        busy.current = false;
        setPending(false);
      }
    }
  };
  return {
    items,
    search,
    setSearch,
    pending,
    error,
    prefillError,
    searchOrders: () => fetchPage(false),
    loadMore: () => fetchPage(true),
    hasMore: (items.page + 1) * items.size < items.totalCount,
  };
}
